import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { apiFetch } from "@/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
    "w-full rounded-xl border border-[#E2E8F4] bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-ember focus:ring-2 focus:ring-ember/20";

const Login = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const resetToken = searchParams.get("reset_token");
    const isForgot = searchParams.get("forgot") === "1";
    const isSignup = !resetToken && !isForgot && searchParams.get("mode") === "signup";

    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "" });

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setError("");
    };

    const heading = resetToken
        ? "Set a new password"
        : isForgot
          ? "Reset your password"
          : isSignup
            ? "Create your account"
            : "Welcome back";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setInfo("");
        try {
            if (resetToken) {
                const data = await apiFetch("/auth/reset-password", {
                    method: "POST",
                    body: JSON.stringify({ token: resetToken, password: form.password }),
                });
                setInfo(data.message);
                toast.success("Password updated.");
                setTimeout(() => setSearchParams({}), 1500);
            } else if (isForgot) {
                const data = await apiFetch("/auth/forgot-password", {
                    method: "POST",
                    body: JSON.stringify({ email: form.email }),
                });
                setInfo(data.message);
            } else if (isSignup) {
                const data = await apiFetch("/auth/register", {
                    method: "POST",
                    body: JSON.stringify(form),
                });
                setUser(data.user);
                toast.success("Account created — welcome to Coaching Crafters AI.");
                navigate("/account");
            } else {
                const data = await apiFetch("/auth/login", {
                    method: "POST",
                    body: JSON.stringify({ email: form.email, password: form.password }),
                });
                setUser(data.user);
                toast.success(`Welcome back, ${data.user.name}.`);
                navigate(data.user.role === "admin" ? "/admin" : "/account");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Seo
                title="Login | Coaching Crafters AI"
                description="Sign in to your Coaching Crafters AI account, or start a free trial."
                path="/login"
            />
            <section
                className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-6 py-16"
                data-testid="login-page"
            >
                <div
                    className="pointer-events-none absolute left-1/2 top-1/3 h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-ember/10 blur-3xl"
                    aria-hidden="true"
                />
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-full max-w-md rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-[0_30px_60px_-24px_rgba(30,79,163,0.25)] sm:p-10"
                >
                    <div className="flex flex-col items-center text-center">
                        <div className="flex items-center gap-3" data-testid="login-brand">
                            <img
                                src="/assets/logo-emblem-dark.png"
                                alt="Coaching Crafters AI emblem"
                                width={52}
                                height={52}
                                className="h-[52px] w-[52px] object-contain"
                            />
                            <span className="whitespace-nowrap font-serif text-2xl font-bold tracking-tight text-ink">
                                Coaching Crafters <span className="text-ember">AI</span>
                            </span>
                        </div>
                        <h1 className="mt-5 font-serif text-2xl font-semibold text-ink" data-testid="login-heading">
                            {heading}
                        </h1>
                        <p className="mt-2 text-sm text-ink/55">
                            {resetToken
                                ? "Choose a new password for your account."
                                : isForgot
                                  ? "Enter your account email and we'll send a reset link."
                                  : isSignup
                                    ? "Start your 14-day free trial — no credit card needed."
                                    : "Sign in to your Coaching Crafters AI account."}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        {isSignup && (
                            <input
                                data-testid="signup-name-input"
                                className={inputCls}
                                placeholder="Full name"
                                value={form.name}
                                onChange={set("name")}
                                required
                            />
                        )}
                        {!resetToken && (
                            <input
                                data-testid="login-email-input"
                                type="email"
                                className={inputCls}
                                placeholder="Email address"
                                value={form.email}
                                onChange={set("email")}
                                required
                            />
                        )}
                        {!isForgot && (
                            <div className="relative">
                                <input
                                    data-testid="login-password-input"
                                    type={showPassword ? "text" : "password"}
                                    className={`${inputCls} pr-12`}
                                    placeholder={resetToken ? "New password (min 8 characters)" : "Password"}
                                    value={form.password}
                                    onChange={set("password")}
                                    required
                                />
                                <button
                                    type="button"
                                    data-testid="toggle-password-button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/35 transition-colors hover:text-ember"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        )}

                        {error && (
                            <p role="alert" data-testid="login-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-700">
                                {error}
                            </p>
                        )}
                        {info && (
                            <p data-testid="login-info" className="rounded-xl bg-ember/5 px-4 py-3 text-xs font-medium text-ember">
                                {info}
                            </p>
                        )}

                        {!isSignup && !isForgot && !resetToken && (
                            <div className="text-right">
                                <button
                                    type="button"
                                    data-testid="forgot-password-link"
                                    onClick={() => setSearchParams({ forgot: "1" })}
                                    className="text-xs font-semibold text-ember hover:underline"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="login-submit-button"
                            disabled={busy}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember py-3.5 text-sm font-semibold text-bone shadow-[0_8px_20px_rgba(30,79,163,0.35)] transition-colors hover:bg-ember-dark disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                            {resetToken ? "Update password" : isForgot ? "Send reset link" : isSignup ? "Create account" : "Log in"}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-ink/55">
                        {isSignup ? (
                            <>
                                Already have an account?{" "}
                                <button data-testid="signup-toggle-link" onClick={() => setSearchParams({})} className="font-semibold text-ember hover:underline">
                                    Log in
                                </button>
                            </>
                        ) : isForgot || resetToken ? (
                            <button data-testid="back-to-login-link" onClick={() => setSearchParams({})} className="font-semibold text-ember hover:underline">
                                Back to log in
                            </button>
                        ) : (
                            <>
                                New to Coaching Crafters AI?{" "}
                                <button data-testid="signup-toggle-link" onClick={() => setSearchParams({ mode: "signup" })} className="font-semibold text-ember hover:underline">
                                    Sign up
                                </button>
                            </>
                        )}
                    </p>
                    <p className="mt-4 text-center text-xs text-ink/35">
                        User IDs are personal — sharing credentials leads to suspension per{" "}
                        <span className="font-medium">Our Policy</span>.
                    </p>
                </motion.div>
            </section>
        </>
    );
};

export default Login;
